import React, { useState, useMemo, useRef, useCallback } from 'react';
import { cotes, centroide, HALO } from '../../utils/cotes';

// ─── Geometry helpers ────────────────────────────────────────────────────────

// Polygon area via shoelace formula
function polygonArea(verts) {
  let area = 0;
  for (let i = 0; i < verts.length; i++) {
    const j = (i + 1) % verts.length;
    area += verts[i].x * verts[j].y - verts[j].x * verts[i].y;
  }
  return Math.abs(area) / 2;
}

function polygonPerimeter(verts) {
  let peri = 0;
  for (let i = 0; i < verts.length; i++) {
    const j = (i + 1) % verts.length;
    peri += Math.hypot(verts[j].x - verts[i].x, verts[j].y - verts[i].y);
  }
  return peri;
}

// Total length of an open polyline (used for interior wall / cloison metré)
function polylineLength(points) {
  if (!points || points.length < 2) return 0;
  let len = 0;
  for (let i = 0; i < points.length - 1; i++) {
    len += Math.hypot(points[i + 1].x - points[i].x, points[i + 1].y - points[i].y);
  }
  return len;
}

// Scale a polyline (anchored at its first point) to an exact total length
function scalePolylineTo(points, newLen) {
  if (!points || points.length < 2) return points;
  const cur = polylineLength(points);
  if (cur < 1e-6) return points;
  const ratio = newLen / cur;
  const a = points[0];
  return points.map((p, i) => i === 0 ? { ...p } : { x: a.x + (p.x - a.x) * ratio, y: a.y + (p.y - a.y) * ratio });
}

// Merge consecutive collinear vertices
function mergeCollinear(verts, tol = 0.001) {
  if (verts.length < 3) return verts;
  const result = [];
  for (let i = 0; i < verts.length; i++) {
    const prev = verts[(i - 1 + verts.length) % verts.length];
    const curr = verts[i];
    const next = verts[(i + 1) % verts.length];
    const cross = (curr.x - prev.x) * (next.y - prev.y) - (curr.y - prev.y) * (next.x - prev.x);
    if (Math.abs(cross) > tol) result.push(curr);
  }
  return result.length >= 3 ? result : verts;
}

// Distance from point P to segment AB → { dist, t }
function pointToSegment(p, a, b) {
  const dx = b.x - a.x, dy = b.y - a.y;
  const lenSq = dx * dx + dy * dy;
  if (lenSq === 0) return { dist: Math.hypot(p.x - a.x, p.y - a.y), t: 0 };
  let t = ((p.x - a.x) * dx + (p.y - a.y) * dy) / lenSq;
  t = Math.max(0, Math.min(1, t));
  const proj = { x: a.x + t * dx, y: a.y + t * dy };
  return { dist: Math.hypot(p.x - proj.x, p.y - proj.y), t };
}

function rectVertices(l, w) {
  return [{ x: 0, y: 0 }, { x: l, y: 0 }, { x: l, y: w }, { x: 0, y: w }];
}

function bbox(verts) {
  let minX = Infinity, minY = Infinity, maxX = -Infinity, maxY = -Infinity;
  for (const v of verts) {
    if (v.x < minX) minX = v.x;
    if (v.y < minY) minY = v.y;
    if (v.x > maxX) maxX = v.x;
    if (v.y > maxY) maxY = v.y;
  }
  return { minX, minY, maxX, maxY, w: maxX - minX, h: maxY - minY };
}

// ─── Stroke → orthogonal polyline (freehand interpretation) ──────────────────

function snapGrid(p, g) {
  return { x: Math.round(p.x / g) * g, y: Math.round(p.y / g) * g };
}

// Perpendicular distance from p to line a→b (for Ramer–Douglas–Peucker)
function perpDist(p, a, b) {
  const dx = b.x - a.x, dy = b.y - a.y;
  const len = Math.hypot(dx, dy);
  if (len === 0) return Math.hypot(p.x - a.x, p.y - a.y);
  return Math.abs((p.x - a.x) * dy - (p.y - a.y) * dx) / len;
}

// Simplify a noisy stroke into its key corners
function rdp(points, epsilon) {
  if (points.length < 3) return points.slice();
  let dmax = 0, index = 0;
  const end = points.length - 1;
  for (let i = 1; i < end; i++) {
    const d = perpDist(points[i], points[0], points[end]);
    if (d > dmax) { index = i; dmax = d; }
  }
  if (dmax > epsilon) {
    const left = rdp(points.slice(0, index + 1), epsilon);
    const right = rdp(points.slice(index), epsilon);
    return left.slice(0, -1).concat(right);
  }
  return [points[0], points[end]];
}

// Snap a point to the nearest outline vertex, else nearest point on an outline edge
function snapToOutline(p, outline, thresh) {
  let best = null, bestD = thresh;
  for (const v of outline) {
    const d = Math.hypot(p.x - v.x, p.y - v.y);
    if (d < bestD) { bestD = d; best = { x: v.x, y: v.y }; }
  }
  if (best) return best;
  bestD = thresh;
  for (let i = 0; i < outline.length; i++) {
    const a = outline[i], b = outline[(i + 1) % outline.length];
    const { dist, t } = pointToSegment(p, a, b);
    if (dist < bestD) { bestD = dist; best = { x: a.x + t * (b.x - a.x), y: a.y + t * (b.y - a.y) }; }
  }
  return best || p;
}

// Turn a raw freehand stroke (meters) into a clean axis-aligned polyline.
// closed=true → returns a polygon (room outline); closed=false → open wall (cloison).
function straightenStroke(raw, { closed, outline = [], grid = 0.1 }) {
  if (!raw || raw.length < 2) return [];
  const pts = rdp(raw, 0.18);
  if (pts.length < 2) return [];

  // Orthogonalize: each segment becomes purely horizontal or vertical
  const out = [snapGrid(pts[0], grid)];
  for (let i = 1; i < pts.length; i++) {
    const prev = out[out.length - 1];
    const cur = pts[i];
    const dx = cur.x - prev.x, dy = cur.y - prev.y;
    const np = snapGrid(
      Math.abs(dx) >= Math.abs(dy) ? { x: cur.x, y: prev.y } : { x: prev.x, y: cur.y },
      grid
    );
    if (Math.hypot(np.x - prev.x, np.y - prev.y) >= grid) out.push(np);
  }
  if (out.length < 2) return [];

  if (closed) {
    if (out.length < 3) return [];
    const first = out[0];
    let last = out[out.length - 1];
    if (Math.hypot(last.x - first.x, last.y - first.y) < 0.6) { out.pop(); last = out[out.length - 1]; }
    if (out.length < 3) return [];
    // Force an orthogonal closing edge back to the first point
    const beforeLast = out[out.length - 2];
    const lastIsHoriz = Math.abs(last.y - beforeLast.y) < 1e-6;
    out[out.length - 1] = snapGrid(
      lastIsHoriz ? { x: first.x, y: last.y } : { x: last.x, y: first.y },
      grid
    );
    return mergeCollinear(out);
  }

  // Open cloison: magnet the two ends onto the outline so it connects wall-to-wall
  if (outline.length >= 3) {
    out[0] = snapToOutline(out[0], outline, 0.5);
    out[out.length - 1] = snapToOutline(out[out.length - 1], outline, 0.5);
  }
  return out;
}

// ─── Wall (cloison) kinds ────────────────────────────────────────────────────

const WALL_KINDS = {
  existing: { label: 'Existante', color: '#475569', dash: null, key: null },
  create:   { label: 'À créer (placo)', color: '#2563eb', dash: null, key: 'murs.cloison_placo_std' },
  demolish: { label: 'À démolir', color: '#dc2626', dash: '5,4', key: 'demolition.demolition_cloison_np' },
};

// ─── Touch length picker (roulette) ─────────────────────────────────────────

const WHEEL_ITEM = 46; // px per row

function LengthWheel({ value, onChange, min = 0.3, max = 15, step = 0.1, rows = 5 }) {
  const ref = useRef(null);
  const settleRef = useRef(null);
  const pad = ((rows - 1) / 2) * WHEEL_ITEM;
  const count = Math.round((max - min) / step) + 1;
  const values = useMemo(
    () => Array.from({ length: count }, (_, i) => Math.round((min + i * step) * 10) / 10),
    [count, min, step]
  );
  const idxOf = useCallback((v) => Math.max(0, Math.min(count - 1, Math.round((v - min) / step))), [count, min, step]);

  // Position the wheel on the current value once mounted
  React.useEffect(() => {
    if (ref.current) ref.current.scrollTop = idxOf(value) * WHEEL_ITEM;
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);

  const handleScroll = useCallback(() => {
    if (!ref.current) return;
    const i = Math.round(ref.current.scrollTop / WHEEL_ITEM);
    const v = values[Math.max(0, Math.min(count - 1, i))];
    if (v != null && v !== value) onChange(v);
  }, [values, count, value, onChange]);

  const nudge = useCallback((dir) => {
    const v = Math.round(Math.max(min, Math.min(max, value + dir * step)) * 10) / 10;
    if (ref.current) {
      clearTimeout(settleRef.current);
      ref.current.scrollTo({ top: idxOf(v) * WHEEL_ITEM, behavior: 'smooth' });
    }
    onChange(v);
  }, [value, step, min, max, idxOf, onChange]);

  return (
    <div className="flex items-center gap-3">
      <button onClick={() => nudge(-1)} className="w-12 h-12 shrink-0 rounded-full bg-slate-100 active:bg-slate-200 text-2xl font-black text-slate-600 flex items-center justify-center">−</button>
      <div className="relative flex-1" style={{ height: WHEEL_ITEM * rows }}>
        {/* highlight band */}
        <div className="absolute left-0 right-0 pointer-events-none border-y-2 border-orange-300 bg-orange-50/40 rounded-lg"
          style={{ top: pad, height: WHEEL_ITEM }} />
        <div ref={ref} onScroll={handleScroll}
          className="h-full overflow-y-scroll no-scrollbar"
          style={{ scrollSnapType: 'y mandatory', WebkitOverflowScrolling: 'touch', touchAction: 'pan-y' }}>
          <div style={{ height: pad }} />
          {values.map(v => {
            const active = v === value;
            return (
              <div key={v} style={{ height: WHEEL_ITEM, scrollSnapAlign: 'center' }}
                className={`flex items-center justify-center font-bold ${active ? 'text-orange-600' : 'text-slate-300'}`}>
                <span style={{ fontSize: active ? 26 : 16, fontWeight: active ? 900 : 600 }}>{v.toFixed(1)}<span className="text-sm ml-1">m</span></span>
              </div>
            );
          })}
          <div style={{ height: pad }} />
        </div>
      </div>
      <button onClick={() => nudge(1)} className="w-12 h-12 shrink-0 rounded-full bg-slate-100 active:bg-slate-200 text-2xl font-black text-slate-600 flex items-center justify-center">+</button>
    </div>
  );
}

// ─── Component ───────────────────────────────────────────────────────────────

export default function RoomCanvas({ vertices: propVertices, height, walls: propWalls, onChange }) {
  const svgRef = useRef(null);
  const containerRef = useRef(null);
  const h = parseFloat(height) || 2.5;

  // A room starts empty (no outline) — the user draws the contour first.
  const hasOutline = !!(propVertices && propVertices.length >= 3);
  const vertices = hasOutline ? propVertices : [];
  const walls = Array.isArray(propWalls) ? propWalls : [];

  // mode: 'select' | 'outline' | 'wall' — start on Contour when the room is empty
  const [mode, setMode] = useState(hasOutline ? 'select' : 'outline');
  // Reset the active tool when switching to a room with/without an outline
  React.useEffect(() => { setMode(hasOutline ? 'select' : 'outline'); setSelected(null); }, [hasOutline]);
  const [draft, setDraft] = useState(null);        // { vertices } during a vertex drag (local only)
  const [strokePts, setStrokePts] = useState([]);  // live freehand stroke (meters)
  const [selected, setSelected] = useState(null);  // { type:'outlineWall'|'cloison', idx }
  const [lengthModal, setLengthModal] = useState(null); // { mode, points?, kind?, idx?, value }
  const [editingHeight, setEditingHeight] = useState(false);
  const [history, setHistory] = useState([]);
  // Guided cote review: walk the outline wall by wall after a freehand commit
  const [review, setReview] = useState(null); // { idx }
  const reviewHistRef = useRef(null);         // wall idx already snapshotted in history

  // Container width
  const [containerWidth, setContainerWidth] = useState(500);
  React.useEffect(() => {
    const el = containerRef.current;
    if (!el) return;
    const ro = new ResizeObserver(entries => {
      for (const entry of entries) setContainerWidth(entry.contentRect.width - 32);
    });
    ro.observe(el);
    return () => ro.disconnect();
  }, []);

  // The vertices currently shown (draft while dragging, else committed)
  const effVertices = draft ? draft.vertices : vertices;

  // ── Undo ──
  const pushHistory = useCallback(() => {
    setHistory(prev => [...prev.slice(-20), JSON.stringify({ vertices, walls })]);
  }, [vertices, walls]);
  const undo = useCallback(() => {
    if (history.length === 0) return;
    const prev = JSON.parse(history[history.length - 1]);
    setHistory(hh => hh.slice(0, -1));
    setSelected(null);
    onChange({ vertices: prev.vertices, height, walls: prev.walls || [] });
  }, [history, onChange, height]);

  // Escape cancels current action; Ctrl/Cmd+Z undoes
  React.useEffect(() => {
    const handleKey = (e) => {
      if (e.key === 'Escape') { setStrokePts([]); setMode('select'); setSelected(null); setReview(null); }
      if ((e.ctrlKey || e.metaKey) && e.key === 'z') { e.preventDefault(); undo(); }
    };
    window.addEventListener('keydown', handleKey);
    return () => window.removeEventListener('keydown', handleKey);
  }, [undo]);

  // ── Metrics ──
  const metrics = useMemo(() => {
    const floorArea = polygonArea(effVertices);
    const perimeter = polygonPerimeter(effVertices);
    let createArea = 0, demolishArea = 0, existingLen = 0;
    for (const w of walls) {
      const len = polylineLength(w.points);
      if (w.kind === 'create') createArea += len * h;
      else if (w.kind === 'demolish') demolishArea += len * h;
      else existingLen += len;
    }
    return { floorArea, perimeter, wallArea: perimeter * h, createArea, demolishArea, existingLen };
  }, [effVertices, walls, h]);

  // ── SVG layout — fit outline (+ live stroke) to ~70% of the canvas ──
  const svgW = containerWidth;
  const svgH = Math.min(420, Math.max(240, svgW * 0.5));
  const bb = useMemo(() => {
    const pts = effVertices.concat(strokePts);
    if (pts.length === 0) return { minX: 0, minY: 0, maxX: 6, maxY: 4, w: 6, h: 4 }; // empty-room drawing frame
    return bbox(pts);
  }, [effVertices, strokePts]);

  const targetFill = 0.72;
  const scale = Math.min(
    (svgW * targetFill) / Math.max(bb.w, 0.1),
    (svgH * targetFill) / Math.max(bb.h, 0.1)
  );
  const offX = (svgW - bb.w * scale) / 2 - bb.minX * scale;
  const offY = (svgH - bb.h * scale) / 2 - bb.minY * scale;

  // Le repère se fige le temps d'un geste. Sinon, en tirant un sommet vers
  // l'extérieur, le contour grandit, l'échelle rétrécit pour le faire tenir,
  // le même déplacement du doigt vaut plus de mètres — et le sommet part à
  // cent mètres en diagonale. Le plan se recadre une fois le doigt levé.
  const repereRef = useRef(null);
  repereRef.current = { scale, offX, offY };
  const geleRef = useRef(null);
  const repere = geleRef.current || repereRef.current;
  const rs = repere.scale, rx = repere.offX, ry = repere.offY;
  const toSvg = useCallback((p) => ({ x: p.x * rs + rx, y: p.y * rs + ry }), [rs, rx, ry]);
  const fromSvg = useCallback((p) => ({ x: (p.x - rx) / rs, y: (p.y - ry) / rs }), [rs, rx, ry]);

  const getSvgPos = useCallback((e) => {
    const svg = svgRef.current;
    if (!svg) return null;
    const rect = svg.getBoundingClientRect();
    return { x: (e.clientX - rect.left) * (svgW / rect.width), y: (e.clientY - rect.top) * (svgH / rect.height) };
  }, [svgW, svgH]);

  // ── Hit testing (in SVG px) ──
  const findVertex = useCallback((svgPos, maxDist) => {
    let best = null, bestDist = maxDist;
    for (let i = 0; i < effVertices.length; i++) {
      const sv = toSvg(effVertices[i]);
      const dist = Math.hypot(svgPos.x - sv.x, svgPos.y - sv.y);
      if (dist < bestDist) { bestDist = dist; best = i; }
    }
    return best;
  }, [effVertices, toSvg]);

  const findOutlineWall = useCallback((svgPos, maxDist) => {
    let best = null, bestDist = maxDist;
    for (let i = 0; i < effVertices.length; i++) {
      const a = toSvg(effVertices[i]);
      const b = toSvg(effVertices[(i + 1) % effVertices.length]);
      const { dist } = pointToSegment(svgPos, a, b);
      if (dist < bestDist) { bestDist = dist; best = i; }
    }
    return best;
  }, [effVertices, toSvg]);

  const findCloison = useCallback((svgPos, maxDist) => {
    let best = null, bestDist = maxDist;
    for (let wi = 0; wi < walls.length; wi++) {
      const pts = walls[wi].points || [];
      for (let i = 0; i < pts.length - 1; i++) {
        const { dist } = pointToSegment(svgPos, toSvg(pts[i]), toSvg(pts[i + 1]));
        if (dist < bestDist) { bestDist = dist; best = wi; }
      }
    }
    return best;
  }, [walls, toSvg]);

  // ── Aimantation d'un sommet qu'on déplace ──
  // Trois aimants, du plus fort au plus faible : l'angle droit avec chaque
  // voisin (un mur presque horizontal ou vertical le devient, quelle que soit
  // sa longueur : 8° de tolérance), l'axe d'un voisin à 18 px d'écran (et non
  // 15 cm, qui ne valaient que 9 px et n'aimantaient rien), l'alignement avec
  // n'importe quel autre sommet du contour, puis la grille de 5 cm.
  const snapVertex = useCallback((idx, pos) => {
    const n = effVertices.length;
    const prev = effVertices[(idx - 1 + n) % n];
    const next = effVertices[(idx + 1) % n];
    let { x, y } = pos;
    const T = Math.max(0.15, 18 / rs);
    const PENTE = Math.tan((8 * Math.PI) / 180);
    const equerre = (v) => {
      const dx = x - v.x, dy = y - v.y;
      if (Math.abs(dy) <= PENTE * Math.abs(dx) || Math.abs(dy) < T) y = v.y;        // mur horizontal
      else if (Math.abs(dx) <= PENTE * Math.abs(dy) || Math.abs(dx) < T) x = v.x;   // mur vertical
    };
    equerre(prev); equerre(next);
    for (let i = 0; i < n; i++) {
      if (i === idx) continue;
      const v = effVertices[i];
      if (Math.abs(x - v.x) < T * 0.6) x = v.x;
      if (Math.abs(y - v.y) < T * 0.6) y = v.y;
    }
    return snapGrid({ x, y }, 0.05);
  }, [effVertices, rs]);

  // ── Guided review: set wall idx to length L keeping its direction, and
  //    translate every following vertex (laser-metré walk). The closing wall
  //    (last one) absorbs the residual error — it is shown read-only.
  const applyReviewLength = useCallback((idx, L) => {
    const n = vertices.length;
    if (idx >= n - 1) return; // closing wall is not editable
    const a = vertices[idx], b = vertices[idx + 1];
    const cur = Math.hypot(b.x - a.x, b.y - a.y);
    if (cur < 1e-6 || Math.abs(L - cur) < 0.005) return;
    if (reviewHistRef.current !== idx) { pushHistory(); reviewHistRef.current = idx; }
    const ux = (b.x - a.x) / cur, uy = (b.y - a.y) / cur;
    const dx = a.x + ux * L - b.x, dy = a.y + uy * L - b.y;
    const verts = vertices.map((v, i) => i <= idx ? v : { x: v.x + dx, y: v.y + dy });
    onChange({ vertices: verts, height, walls });
  }, [vertices, height, walls, onChange, pushHistory]);

  // ── Insert a corner on an outline wall (missing pan / pan coupé) ──
  const insertVertexOnWall = useCallback((idx, svgPos) => {
    const n = effVertices.length;
    const a = effVertices[idx], b = effVertices[(idx + 1) % n];
    let p;
    if (svgPos) {
      const { t } = pointToSegment(svgPos, toSvg(a), toSvg(b));
      const tt = Math.max(0.15, Math.min(0.85, t));
      p = { x: a.x + tt * (b.x - a.x), y: a.y + tt * (b.y - a.y) };
    } else {
      p = { x: (a.x + b.x) / 2, y: (a.y + b.y) / 2 };
    }
    p = snapGrid(p, 0.05);
    pushHistory();
    const verts = [...vertices.slice(0, idx + 1), p, ...vertices.slice(idx + 1)];
    onChange({ vertices: verts, height, walls });
    setSelected(null);
    setReview(r => (r && r.idx > idx ? { idx: r.idx + 1 } : r));
  }, [effVertices, vertices, height, walls, onChange, pushHistory, toSvg]);

  // ── Pointer interaction ──
  const strokeRef = useRef(null);     // raw meter points of the active stroke
  const dragRef = useRef(null);       // index of the outline vertex being dragged
  const activePointerRef = useRef(null);

  const coarse = useCallback((e) => {
    const t = e?.pointerType || 'mouse';
    return t === 'touch' || t === 'pen';
  }, []);

  const handlePointerDown = useCallback((e) => {
    if (e.button !== undefined && e.button !== 0) return;
    const pos = getSvgPos(e);
    if (!pos) return;

    // Drawing modes: begin a freehand stroke
    if (mode === 'outline' || mode === 'wall') {
      geleRef.current = repereRef.current;
      strokeRef.current = [fromSvg(pos)];
      setStrokePts(strokeRef.current.slice());
      activePointerRef.current = e.pointerId;
      e.currentTarget.setPointerCapture?.(e.pointerId);
      e.preventDefault();
      return;
    }

    // Select mode: vertex drag has priority, then cloison, then outline wall
    const vRadius = coarse(e) ? 26 : 14;
    const wRadius = coarse(e) ? 30 : 18;

    const vIdx = findVertex(pos, vRadius);
    if (vIdx !== null) {
      pushHistory();
      setDraft({ vertices: effVertices.map(v => ({ ...v })) });
      geleRef.current = repereRef.current;
      dragRef.current = vIdx;
      setSelected(null);
      activePointerRef.current = e.pointerId;
      e.currentTarget.setPointerCapture?.(e.pointerId);
      e.preventDefault();
      return;
    }

    const cIdx = findCloison(pos, wRadius);
    if (cIdx !== null) { setSelected({ type: 'cloison', idx: cIdx }); return; }

    const owIdx = findOutlineWall(pos, wRadius);
    if (owIdx !== null) {
      // During the guided review, tapping a wall jumps the review to it
      if (review) { setReview({ idx: owIdx }); setSelected(null); return; }
      setSelected({ type: 'outlineWall', idx: owIdx });
      return;
    }

    setSelected(null);
  }, [mode, getSvgPos, fromSvg, coarse, findVertex, findCloison, findOutlineWall, pushHistory, effVertices, review]);

  const handlePointerMove = useCallback((e) => {
    if (activePointerRef.current !== null && e.pointerId !== activePointerRef.current) return;
    const pos = getSvgPos(e);
    if (!pos) return;

    // Freehand stroke in progress
    if (strokeRef.current) {
      const m = fromSvg(pos);
      const arr = strokeRef.current;
      const last = arr[arr.length - 1];
      if (!last || Math.hypot(m.x - last.x, m.y - last.y) > 0.03) {
        arr.push(m);
        setStrokePts(arr.slice());
      }
      e.preventDefault();
      return;
    }

    // Vertex drag (local draft only — parent untouched until release)
    if (dragRef.current !== null) {
      const idx = dragRef.current;
      const snapped = snapVertex(idx, fromSvg(pos));
      setDraft(d => {
        const verts = (d ? d.vertices : effVertices).map(v => ({ ...v }));
        verts[idx] = snapped;
        return { vertices: verts };
      });
      e.preventDefault();
    }
  }, [getSvgPos, fromSvg, snapVertex, effVertices]);

  const handlePointerUp = useCallback((e) => {
    geleRef.current = null;
    if (activePointerRef.current === e.pointerId) {
      e.currentTarget.releasePointerCapture?.(e.pointerId);
      activePointerRef.current = null;
    }

    // Commit a freehand stroke
    if (strokeRef.current) {
      const raw = strokeRef.current;
      strokeRef.current = null;
      setStrokePts([]);
      if (mode === 'outline') {
        const poly = straightenStroke(raw, { closed: true, grid: 0.1 });
        // Rectangles (or a sparse/degenerate touch stroke) → ask for W×H from the
        // stroke's bounding box (robust even with only 2 captured points).
        // Genuinely complex shapes (L-rooms, ≥5 corners) commit the polygon directly.
        if (poly.length < 5) {
          let minX = Infinity, minY = Infinity, maxX = -Infinity, maxY = -Infinity;
          for (const p of raw) {
            if (p.x < minX) minX = p.x; if (p.y < minY) minY = p.y;
            if (p.x > maxX) maxX = p.x; if (p.y > maxY) maxY = p.y;
          }
          let w = Math.round((maxX - minX) * 10) / 10;
          let hh = Math.round((maxY - minY) * 10) / 10;
          if (!(w >= 0.5)) w = 4;
          if (!(hh >= 0.5)) hh = 3;
          setLengthModal({ mode: 'rectOutline', w, h: hh });
        } else {
          pushHistory();
          onChange({ vertices: poly, height, walls });
          setMode('select');
          // Complex outline → walk the cotes wall by wall right away
          reviewHistRef.current = null;
          setReview({ idx: 0 });
        }
        // rectOutline modal path intentionally stays in 'outline' (Annuler → redraw)
      } else if (mode === 'wall') {
        const line = straightenStroke(raw, { closed: false, outline: vertices, grid: 0.1 });
        if (line.length >= 2 && polylineLength(line) > 0.2) {
          // Open the length picker before committing the new cloison
          setLengthModal({ mode: 'newWall', points: line, kind: 'create', value: Math.round(polylineLength(line) * 10) / 10 });
        }
        setMode('select');
      }
      e.preventDefault();
      return;
    }

    // Commit a vertex drag
    if (dragRef.current !== null) {
      const idx = dragRef.current;
      dragRef.current = null;
      const verts = draft ? mergeCollinear(draft.vertices) : vertices;
      setDraft(null);
      onChange({ vertices: verts, height, walls });
      e.preventDefault();
    }
  }, [mode, draft, vertices, walls, height, onChange, pushHistory]);

  // Double-tap an outline wall → insert a corner there (pan oublié / pan coupé)
  const handleDoubleClick = useCallback((e) => {
    if (mode !== 'select' || effVertices.length < 3) return;
    const pos = getSvgPos(e);
    if (!pos) return;
    const owIdx = findOutlineWall(pos, 24);
    if (owIdx !== null) insertVertexOnWall(owIdx, pos);
  }, [mode, effVertices.length, getSvgPos, findOutlineWall, insertVertexOnWall]);

  const handlePointerCancel = useCallback(() => {
    strokeRef.current = null;
    dragRef.current = null;
    activePointerRef.current = null;
    setStrokePts([]);
    setDraft(null);
  }, []);

  // ── Wall (cloison) operations ──
  const setWallKind = useCallback((idx, kind) => {
    pushHistory();
    onChange({ vertices, height, walls: walls.map((w, i) => i === idx ? { ...w, kind } : w) });
  }, [vertices, height, walls, onChange, pushHistory]);

  const deleteWall = useCallback((idx) => {
    pushHistory();
    onChange({ vertices, height, walls: walls.filter((_, i) => i !== idx) });
    setSelected(null);
  }, [vertices, height, walls, onChange, pushHistory]);

  // ── Scale a selected outline wall to an exact length ──
  const setOutlineWallLength = useCallback((idx, newLen) => {
    const a = vertices[idx], b = vertices[(idx + 1) % vertices.length];
    const curLen = Math.hypot(b.x - a.x, b.y - a.y);
    if (!newLen || Math.abs(newLen - curLen) < 0.01 || curLen < 1e-6) return;
    const ratio = newLen / curLen;
    pushHistory();
    const verts = vertices.map((v, i) => i === (idx + 1) % vertices.length
      ? { x: a.x + (b.x - a.x) * ratio, y: a.y + (b.y - a.y) * ratio }
      : v);
    onChange({ vertices: mergeCollinear(verts), height, walls });
  }, [vertices, height, walls, onChange, pushHistory]);

  // ── Length picker (roulette) ──
  const openWallLength = useCallback((idx) => {
    const w = walls[idx]; if (!w) return;
    setLengthModal({ mode: 'editWall', idx, value: Math.round(polylineLength(w.points) * 10) / 10 });
  }, [walls]);

  const openOutlineLength = useCallback((idx) => {
    const a = effVertices[idx], b = effVertices[(idx + 1) % effVertices.length];
    setLengthModal({ mode: 'editOutline', idx, value: Math.round(Math.hypot(b.x - a.x, b.y - a.y) * 10) / 10 });
  }, [effVertices]);

  const confirmLength = useCallback(() => {
    const m = lengthModal; if (!m) return;
    if (m.mode === 'newWall') {
      pushHistory();
      const pts = scalePolylineTo(m.points, m.value);
      onChange({ vertices, height, walls: [...walls, { id: Date.now(), points: pts, kind: m.kind || 'create' }] });
      setSelected({ type: 'cloison', idx: walls.length });
    } else if (m.mode === 'editWall' && walls[m.idx]) {
      pushHistory();
      const pts = scalePolylineTo(walls[m.idx].points, m.value);
      onChange({ vertices, height, walls: walls.map((w, i) => i === m.idx ? { ...w, points: pts } : w) });
    } else if (m.mode === 'editOutline') {
      setOutlineWallLength(m.idx, m.value);
    } else if (m.mode === 'rectOutline') {
      pushHistory();
      onChange({ vertices: rectVertices(m.w, m.h), height, walls });
    }
    setLengthModal(null);
  }, [lengthModal, vertices, height, walls, onChange, pushHistory, setOutlineWallLength]);

  // ── Derived render data ──
  const pathD = useMemo(() => effVertices.map((v, i) => {
    const sv = toSvg(v);
    return `${i === 0 ? 'M' : 'L'}${sv.x.toFixed(1)},${sv.y.toFixed(1)}`;
  }).join(' ') + ' Z', [effVertices, toSvg]);

  const strokePath = useMemo(() => {
    if (strokePts.length < 2) return null;
    return strokePts.map((p, i) => {
      const s = toSvg(p);
      return `${i === 0 ? 'M' : 'L'}${s.x.toFixed(1)},${s.y.toFixed(1)}`;
    }).join(' ');
  }, [strokePts, toSvg]);

  // Les cotes se lisent hors de la pièce, à côté de leur mur (utils/cotes).
  const outlineWallLabels = useMemo(() => cotes(effVertices, toSvg, { ecart: 11, minLen: 0.15, minPx: 16 }), [effVertices, toSvg]);

  // Live preview of the cloison whose length is being set in the modal
  const modalPreview = useMemo(() => {
    if (!lengthModal) return null;
    if (lengthModal.mode === 'newWall') return scalePolylineTo(lengthModal.points, lengthModal.value);
    if (lengthModal.mode === 'editWall' && walls[lengthModal.idx]) return scalePolylineTo(walls[lengthModal.idx].points, lengthModal.value);
    return null;
  }, [lengthModal, walls]);

  const modalPreviewPath = useMemo(() => {
    if (!modalPreview) return null;
    return modalPreview.map((p, i) => { const s = toSvg(p); return `${i === 0 ? 'M' : 'L'}${s.x.toFixed(1)},${s.y.toFixed(1)}`; }).join(' ');
  }, [modalPreview, toSvg]);

  const drawing = mode === 'outline' || mode === 'wall';

  return (
    <div ref={containerRef} className="bg-white rounded-xl border-2 border-slate-200 p-4">
      {/* ── Toolbar ── */}
      <div className="flex items-center gap-2 mb-3 flex-wrap">
        <div className="flex gap-1 bg-slate-100 rounded-lg p-1">
          {[
            { id: 'select', label: 'Modifier', icon: '✋' },
            { id: 'wall', label: 'Cloison', icon: '➕' },
            { id: 'outline', label: 'Contour', icon: '✏️' },
          ]
            // Until the room has an outline, only the Contour tool is offered
            .filter(m => hasOutline || m.id === 'outline')
            .map(m => (
              <button key={m.id} onClick={() => { setMode(m.id); setSelected(null); }}
                className={`px-3 py-2 rounded-md text-xs font-bold transition-colors min-h-[40px] ${
                  mode === m.id ? 'bg-white text-slate-900 shadow-sm' : 'text-slate-500 hover:text-slate-700'
                }`}>
                <span className="mr-1">{m.icon}</span>{m.label}
              </button>
            ))}
        </div>
        {hasOutline && !review && (
          <button onClick={() => { setMode('select'); setSelected(null); reviewHistRef.current = null; setReview({ idx: 0 }); }}
            className="px-3 py-2 bg-orange-100 hover:bg-orange-200 rounded-lg text-xs font-bold text-orange-700 min-h-[40px]">
            📏 Cotes
          </button>
        )}
        <button onClick={undo} disabled={history.length === 0}
          className="flex items-center gap-1 px-3 py-2 bg-slate-100 hover:bg-slate-200 disabled:opacity-30 rounded-lg text-xs font-semibold text-slate-600 min-h-[40px]" title="Ctrl+Z">
          <svg className="w-3.5 h-3.5" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2.5" strokeLinecap="round" strokeLinejoin="round"><path d="M3 10h10a5 5 0 0 1 0 10H9" /><path d="M3 10l4-4" /><path d="M3 10l4 4" /></svg>
          Annuler
        </button>
        <span className="text-xs text-slate-400 ml-auto">
          {drawing
            ? (mode === 'wall' ? 'Tracez une cloison au doigt' : 'Tracez le contour de la pièce')
            : `${effVertices.length} sommets · ${walls.length} cloison${walls.length > 1 ? 's' : ''}`}
        </span>
      </div>

      <div className="flex flex-col md:flex-row gap-4">
        {/* SVG */}
        <div className="flex-1 min-w-0">
          <svg ref={svgRef} viewBox={`0 0 ${svgW} ${svgH}`}
            className={`w-full ${drawing ? 'cursor-crosshair' : 'cursor-pointer'}`}
            style={{ touchAction: 'none', userSelect: 'none', WebkitUserSelect: 'none' }}
            onPointerDown={handlePointerDown} onPointerMove={handlePointerMove}
            onPointerUp={handlePointerUp} onPointerCancel={handlePointerCancel}
            onDoubleClick={handleDoubleClick}>
            <defs>
              <pattern id="grid" width="20" height="20" patternUnits="userSpaceOnUse">
                <path d="M 20 0 L 0 0 0 20" fill="none" stroke="#f1f5f9" strokeWidth="0.5" />
              </pattern>
            </defs>
            <rect width={svgW} height={svgH} fill="url(#grid)" />

            {/* Empty room — invite to draw the contour */}
            {!hasOutline && strokePts.length === 0 && (
              <g style={{ pointerEvents: 'none' }}>
                <text x={svgW / 2} y={svgH / 2 - 6} textAnchor="middle" fill="#cbd5e1" fontSize="30">✏️</text>
                <text x={svgW / 2} y={svgH / 2 + 22} textAnchor="middle" fill="#94a3b8" fontSize="13" fontWeight="700">Dessine le contour de la pièce</text>
                <text x={svgW / 2} y={svgH / 2 + 40} textAnchor="middle" fill="#cbd5e1" fontSize="11">au doigt, puis règle les dimensions</text>
              </g>
            )}

            {/* Room outline */}
            {hasOutline && <path d={pathD} fill="#fff7ed" stroke="#ea580c" strokeWidth="2" strokeLinejoin="round" />}

            {/* Outline wall selection highlight */}
            {selected?.type === 'outlineWall' && (() => {
              const a = toSvg(effVertices[selected.idx]);
              const b = toSvg(effVertices[(selected.idx + 1) % effVertices.length]);
              return <line x1={a.x} y1={a.y} x2={b.x} y2={b.y} stroke="#3b82f6" strokeWidth="5" strokeLinecap="round" style={{ pointerEvents: 'none' }} />;
            })()}

            {/* Guided review: highlight the wall being measured */}
            {review && effVertices.length >= 3 && (() => {
              const idx = Math.min(review.idx, effVertices.length - 1);
              const a = toSvg(effVertices[idx]);
              const b = toSvg(effVertices[(idx + 1) % effVertices.length]);
              return <line x1={a.x} y1={a.y} x2={b.x} y2={b.y} stroke="#f97316" strokeWidth="7" strokeLinecap="round" opacity="0.9" style={{ pointerEvents: 'none' }} />;
            })()}

            {/* Interior walls (cloisons) */}
            {walls.map((w, wi) => {
              const kind = WALL_KINDS[w.kind] || WALL_KINDS.create;
              const d = (w.points || []).map((p, i) => { const s = toSvg(p); return `${i === 0 ? 'M' : 'L'}${s.x.toFixed(1)},${s.y.toFixed(1)}`; }).join(' ');
              const isSel = selected?.type === 'cloison' && selected.idx === wi;
              const len = polylineLength(w.points);
              const mid = w.points?.[Math.floor(w.points.length / 2)] ? toSvg(w.points[Math.floor((w.points.length - 1) / 2)]) : null;
              return (
                <g key={w.id ?? wi} style={{ pointerEvents: 'none' }}>
                  {isSel && <path d={d} fill="none" stroke="#93c5fd" strokeWidth="8" strokeLinecap="round" strokeLinejoin="round" />}
                  <path d={d} fill="none" stroke={kind.color} strokeWidth="3.5" strokeLinecap="round" strokeLinejoin="round" strokeDasharray={kind.dash || undefined} />
                  {mid && len > 0.3 && (
                    <text x={mid.x} y={mid.y - 6} textAnchor="middle" fill={kind.color} fontSize="10" fontWeight="700" {...HALO}>{len.toFixed(1)}m</text>
                  )}
                </g>
              );
            })}

            {/* Live freehand stroke */}
            {strokePath && (
              <path d={strokePath} fill="none" stroke={mode === 'wall' ? '#2563eb' : '#ea580c'}
                strokeWidth="2.5" strokeLinecap="round" strokeLinejoin="round" strokeDasharray="2,4" opacity="0.7" />
            )}

            {/* Length-picker preview (updates live with the roulette) */}
            {modalPreviewPath && (
              <path d={modalPreviewPath} fill="none" stroke="#2563eb" strokeWidth="4"
                strokeLinecap="round" strokeLinejoin="round" strokeDasharray="6,4" />
            )}

            {/* Vertex handles (select mode only) */}
            {!drawing && effVertices.map((v, i) => {
              const sv = toSvg(v);
              const isDrag = dragRef.current === i;
              return (
                <circle key={`v${i}`} cx={sv.x} cy={sv.y} r={isDrag ? 7 : 5}
                  fill={isDrag ? '#3b82f6' : '#fff'} stroke={isDrag ? '#1d4ed8' : '#ea580c'}
                  strokeWidth={isDrag ? 2.5 : 1.5} style={{ pointerEvents: 'none' }} />
              );
            })}

            {/* Outline wall length labels */}
            {!drawing && outlineWallLabels.map(w => {
              const isReviewed = review && Math.min(review.idx, effVertices.length - 1) === w.i;
              return (
                <text key={w.i} x={w.x} y={w.y} textAnchor={w.anchor} dominantBaseline={w.baseline}
                  fill={isReviewed ? '#ea580c' : '#64748b'} fontSize={isReviewed ? 13 : 10} fontWeight={isReviewed ? 900 : 700}
                  {...HALO} style={{ pointerEvents: 'none' }}>
                  {w.len.toFixed(2)} m
                </text>
              );
            })}

            {/* Center: area */}
            {hasOutline && effVertices.length > 0 && (() => {
              const sc = toSvg(centroide(effVertices));
              return <text x={sc.x} y={sc.y} textAnchor="middle" dominantBaseline="middle" fill="#ea580c" fontSize="18" fontWeight="900" {...HALO} strokeWidth={4} style={{ pointerEvents: 'none' }}>{metrics.floorArea.toFixed(1)} m²</text>;
            })()}
          </svg>
        </div>

        {/* Side panel */}
        <div className="md:w-56 shrink-0 space-y-3">
          {/* Guided cote review */}
          {review && vertices.length >= 3 && (() => {
            const n = vertices.length;
            const idx = Math.min(review.idx, n - 1);
            const isLast = idx === n - 1;
            const a = vertices[idx], b = vertices[(idx + 1) % n];
            const len = Math.hypot(b.x - a.x, b.y - a.y);
            // Closing wall off-axis amount = residual error of the walk
            const la = vertices[n - 1], lb = vertices[0];
            const off = Math.min(Math.abs(lb.x - la.x), Math.abs(lb.y - la.y));
            return (
              <div className="bg-orange-50 rounded-lg p-3 border-2 border-orange-300">
                <div className="flex items-center justify-between mb-1">
                  <span className="text-[10px] font-black text-orange-600 uppercase">Cotes · mur {idx + 1}/{n}</span>
                  <button onClick={() => setReview(null)} className="px-2 py-1 text-xs font-bold text-slate-400 hover:text-slate-600" aria-label="Fermer la revue">✕</button>
                </div>
                {isLast ? (
                  <div className="text-center py-2">
                    <div className="text-2xl font-black text-slate-700">{len.toFixed(2)} m</div>
                    <div className="text-[11px] text-slate-500 mt-1">Mur de fermeture — il absorbe l'écart des autres cotes</div>
                    {off > 0.05 && (
                      <div className="text-[11px] font-bold text-red-600 mt-1">⚠️ Ne boucle pas d'équerre (écart {off.toFixed(2)} m)</div>
                    )}
                  </div>
                ) : (
                  <LengthWheel key={idx} rows={3} value={Math.round(len * 10) / 10}
                    onChange={(v) => applyReviewLength(idx, v)} />
                )}
                <div className="flex gap-2 mt-3">
                  <button onClick={() => setReview({ idx: idx - 1 })} disabled={idx === 0}
                    className="px-3 py-2 bg-white border border-orange-200 rounded-lg text-sm font-bold text-slate-600 disabled:opacity-30 min-h-[44px]">←</button>
                  {idx < n - 1 ? (
                    <button onClick={() => setReview({ idx: idx + 1 })}
                      className="flex-1 px-3 py-2 bg-orange-600 hover:bg-orange-700 text-white rounded-lg text-sm font-bold min-h-[44px]">Mur suivant →</button>
                  ) : (
                    <button onClick={() => setReview(null)}
                      className="flex-1 px-3 py-2 bg-green-600 hover:bg-green-700 text-white rounded-lg text-sm font-bold min-h-[44px]">Terminer ✓</button>
                  )}
                </div>
              </div>
            );
          })()}

          {/* Selection editor */}
          {!review && selected?.type === 'cloison' && walls[selected.idx] && (
            <div className="bg-blue-50 rounded-lg p-3 border border-blue-200">
              <div className="text-[10px] font-semibold text-blue-500 uppercase mb-2">Cloison sélectionnée</div>
              <div className="space-y-1.5">
                {Object.entries(WALL_KINDS).map(([k, v]) => (
                  <button key={k} onClick={() => setWallKind(selected.idx, k)}
                    className={`w-full text-left px-3 py-2 rounded-lg text-xs font-bold min-h-[40px] transition-colors ${
                      (walls[selected.idx].kind || 'create') === k ? 'text-white' : 'bg-white text-slate-600 hover:bg-slate-50'
                    }`}
                    style={(walls[selected.idx].kind || 'create') === k ? { background: v.color } : undefined}>
                    {v.label}
                  </button>
                ))}
                <button onClick={() => openWallLength(selected.idx)}
                  className="w-full flex items-center justify-between px-3 py-2 bg-white hover:bg-slate-50 text-slate-700 rounded-lg text-xs font-bold min-h-[40px] border border-slate-200">
                  <span>Longueur</span>
                  <span className="text-orange-600">{polylineLength(walls[selected.idx].points).toFixed(1)} m ›</span>
                </button>
                <button onClick={() => deleteWall(selected.idx)}
                  className="w-full px-3 py-2 bg-red-50 hover:bg-red-100 text-red-600 rounded-lg text-xs font-bold min-h-[40px]">
                  Supprimer la cloison
                </button>
              </div>
            </div>
          )}

          {!review && selected?.type === 'outlineWall' && (
            <div className="bg-slate-50 rounded-lg p-3 border border-slate-200 space-y-1.5">
              <div className="text-[10px] font-semibold text-slate-400 uppercase mb-2">Mur du contour</div>
              <button onClick={() => openOutlineLength(selected.idx)}
                className="w-full flex items-center justify-between px-3 py-2 bg-white hover:bg-slate-50 text-slate-700 rounded-lg text-sm font-bold min-h-[40px] border border-slate-200">
                <span>Longueur</span>
                <span className="text-orange-600">
                  {Math.hypot(
                    effVertices[(selected.idx + 1) % effVertices.length].x - effVertices[selected.idx].x,
                    effVertices[(selected.idx + 1) % effVertices.length].y - effVertices[selected.idx].y
                  ).toFixed(1)} m ›
                </span>
              </button>
              <button onClick={() => insertVertexOnWall(selected.idx)}
                className="w-full px-3 py-2 bg-white hover:bg-slate-50 text-slate-700 rounded-lg text-xs font-bold min-h-[40px] border border-slate-200">
                ＋ Ajouter un coin sur ce mur
              </button>
            </div>
          )}

          {/* Height */}
          <div className="bg-slate-50 rounded-lg p-2 flex items-center justify-between">
            <span className="text-[10px] font-semibold text-slate-400 uppercase">Hauteur</span>
            {editingHeight ? (
              <input type="number" step="0.1" min="1" max="6" autoFocus value={height}
                onChange={e => onChange({ vertices, height: e.target.value, walls })}
                onBlur={() => setEditingHeight(false)} onKeyDown={e => e.key === 'Enter' && setEditingHeight(false)}
                className="w-16 px-2 py-1 border-2 border-blue-400 rounded text-sm font-bold text-center outline-none" />
            ) : (
              <button onClick={() => setEditingHeight(true)} className="text-sm font-black text-slate-700">{h.toFixed(1)} m</button>
            )}
          </div>

          {/* Surfaces */}
          <div>
            <div className="text-[10px] font-semibold text-slate-400 uppercase mb-1">Surfaces</div>
            <div className="grid grid-cols-2 gap-2 text-center">
              <div className="bg-orange-50 rounded-lg p-2">
                <div className="text-sm font-black text-orange-600">{metrics.floorArea.toFixed(1)}</div>
                <div className="text-[10px] text-slate-500">m² sol</div>
              </div>
              <div className="bg-slate-50 rounded-lg p-2">
                <div className="text-sm font-black text-slate-700">{metrics.wallArea.toFixed(1)}</div>
                <div className="text-[10px] text-slate-500">m² murs</div>
              </div>
              <div className="bg-slate-50 rounded-lg p-2">
                <div className="text-sm font-black text-slate-700">{metrics.floorArea.toFixed(1)}</div>
                <div className="text-[10px] text-slate-500">m² plafond</div>
              </div>
              <div className="bg-slate-50 rounded-lg p-2">
                <div className="text-sm font-black text-slate-700">{metrics.perimeter.toFixed(1)}</div>
                <div className="text-[10px] text-slate-500">ml périm.</div>
              </div>
            </div>
          </div>

          {/* Cloison metré */}
          {(metrics.createArea > 0.05 || metrics.demolishArea > 0.05) && (
            <div>
              <div className="text-[10px] font-semibold text-slate-400 uppercase mb-1">Cloisons</div>
              <div className="space-y-1">
                {metrics.createArea > 0.05 && (
                  <div className="flex items-center justify-between bg-blue-50 rounded-lg px-3 py-2">
                    <span className="text-[11px] font-semibold text-blue-700">À créer (placo)</span>
                    <span className="text-sm font-black text-blue-700">{metrics.createArea.toFixed(1)} m²</span>
                  </div>
                )}
                {metrics.demolishArea > 0.05 && (
                  <div className="flex items-center justify-between bg-red-50 rounded-lg px-3 py-2">
                    <span className="text-[11px] font-semibold text-red-700">À démolir</span>
                    <span className="text-sm font-black text-red-700">{metrics.demolishArea.toFixed(1)} m²</span>
                  </div>
                )}
              </div>
            </div>
          )}
        </div>
      </div>

      {/* ── Length picker modal (roulette) ── */}
      {lengthModal && (
        // z-[60]: must sit above the fixed totals bar of ProDevis (z-50), which
        // otherwise covers the Valider/Annuler buttons and swallows their taps
        <div className="fixed inset-0 z-[60] flex items-end sm:items-center justify-center bg-black/40 p-4"
          onPointerDown={() => setLengthModal(null)}>
          <div className="bg-white rounded-2xl shadow-2xl w-full max-w-sm p-5 max-h-[85vh] overflow-y-auto" onPointerDown={e => e.stopPropagation()}>
            {lengthModal.mode === 'rectOutline' ? (
              <>
                <div className="text-center mb-1 text-sm font-bold text-slate-900">Dimensions de la pièce</div>
                <div className="text-center text-3xl font-black text-orange-600 mb-3">
                  {lengthModal.w.toFixed(1)} × {lengthModal.h.toFixed(1)} m
                  <span className="block text-xs font-semibold text-slate-400 mt-1">{(lengthModal.w * lengthModal.h).toFixed(1)} m²</span>
                </div>
                <div className="text-[11px] font-bold text-slate-500 uppercase mb-1 text-center">Largeur</div>
                <LengthWheel rows={3} value={lengthModal.w} onChange={(v) => setLengthModal(m => (m ? { ...m, w: v } : m))} />
                <div className="text-[11px] font-bold text-slate-500 uppercase mb-1 mt-3 text-center">Profondeur</div>
                <LengthWheel rows={3} value={lengthModal.h} onChange={(v) => setLengthModal(m => (m ? { ...m, h: v } : m))} />
              </>
            ) : (
              <>
                <div className="text-center mb-1 text-sm font-bold text-slate-900">
                  {lengthModal.mode === 'newWall' ? 'Longueur de la cloison' : 'Régler la longueur'}
                </div>
                <div className="text-center text-3xl font-black text-orange-600 mb-3">{lengthModal.value.toFixed(1)} m</div>

                <LengthWheel value={lengthModal.value} onChange={(v) => setLengthModal(m => (m ? { ...m, value: v } : m))} />

                {lengthModal.mode === 'newWall' && (
                  <div className="flex gap-2 justify-center mt-4">
                    {['existing', 'create', 'demolish'].map(k => (
                      <button key={k} onClick={() => setLengthModal(m => ({ ...m, kind: k }))}
                        className={`px-3 py-2 rounded-lg text-xs font-bold min-h-[40px] ${(lengthModal.kind || 'create') === k ? 'text-white' : 'bg-slate-100 text-slate-500'}`}
                        style={(lengthModal.kind || 'create') === k ? { background: WALL_KINDS[k].color } : undefined}>
                        {WALL_KINDS[k].label}
                      </button>
                    ))}
                  </div>
                )}
              </>
            )}

            <div className="flex gap-2 mt-5">
              <button onClick={() => setLengthModal(null)} className="flex-1 py-3 bg-slate-100 hover:bg-slate-200 rounded-xl text-sm font-bold text-slate-600 min-h-[48px]">Annuler</button>
              <button onClick={confirmLength} className="flex-1 py-3 bg-orange-600 hover:bg-orange-700 text-white rounded-xl text-sm font-bold min-h-[48px]">Valider</button>
            </div>
          </div>
        </div>
      )}
    </div>
  );
}

// Export helpers for ProDevis / ProDossier
export { polygonArea, polygonPerimeter, polylineLength, rectVertices, WALL_KINDS };
